/**
 * Which clinic the local Dexie cache currently holds data for.
 *
 * ## Why this has to be tracked
 *
 * The offline cache is single-clinic, and mid-session switches keep it honest
 * by flushing (see ClinicContext). But a session can also END without a clean
 * sign-out — the tab is closed, the laptop sleeps, the browser is killed. The
 * cache survives that, still full of whichever clinic was last active.
 *
 * So at LOGIN the cache holds one clinic's PHI and the owner is about to pick a
 * clinic, possibly a different one. Without a marker there is no way to tell
 * whether the two agree, and unfiltered local reads (StartVisitPage does a bare
 * `db.patients.toArray()`) would serve the previous clinic's patients under the
 * newly chosen one's name.
 *
 * `clearPhiTables` clears this alongside the data, so "no marker" correctly
 * means "no clinic data on this device".
 */

const STORAGE_KEY = "kaagazy_cache_clinic_id";

export function getCacheClinicId(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    // Private mode / blocked storage. Returning null means callers treat the
    // cache as foreign and flush — the safe direction to fail in.
    return null;
  }
}

/** Record the clinic the cache was hydrated for. Call AFTER a successful pull. */
export function setCacheClinicId(clinicId: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, clinicId);
  } catch {
    /* non-fatal — worst case the next login flushes unnecessarily */
  }
}

export function clearCacheClinicId(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* non-fatal */
  }
}

/**
 * True when the cache definitely holds a DIFFERENT clinic's data.
 *
 * A null marker is not "foreign" — it means empty or unknown, which needs a
 * flush for a different reason (we cannot vouch for what is there) but is not
 * evidence of another clinic's rows. Callers distinguish the two because a
 * foreign cache with unsynced writes must not be destroyed.
 */
export function cacheBelongsToAnotherClinic(clinicId: string): boolean {
  const cached = getCacheClinicId();
  return cached !== null && cached !== clinicId;
}
